import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Download, Loader2, Mic2, Play, RotateCcw, UploadCloud } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ConsentModal, CONSENT_VERSION } from "@/components/create/ConsentModal";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import {
  listVoicePresets,
  listVoiceJobs,
  createVoiceJob,
  convertJobAudio,
  updateVoiceJobStatus,
  getVoiceJobSignedUrl,
  getVoiceQuota,
} from "@/lib/voice-change.functions";
import { checkDuration, detectVoiceEnv, type VoiceEnvReport } from "@/lib/voiceChange/env";
import { readVoicePresetId } from "@/components/voice/voiceSelection";

export const Route = createFileRoute("/_authenticated/voice-change")({
  head: () => ({
    meta: [
      { title: "音声変換（ボイスチェンジ） | KAGEM" },
      {
        name: "description",
        content:
          "アップロードした動画の声だけを別の声質に変換します。映像はそのまま、ブラウザ内で高速に処理します。",
      },
      { property: "og:title", content: "音声変換（ボイスチェンジ） | KAGEM" },
      {
        property: "og:description",
        content: "動画の声だけをAIで変換。映像は無劣化のまま差し替えます。",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: VoiceChangePage,
});

type Phase = "idle" | "engine" | "extracting" | "converting" | "muxing" | "done" | "error";

/** 進捗の重み配分: 抽出 0-15% / ElevenLabs 15-85% / mux 85-95% / 完成動画のアップロード 95-100% */
const W = { extractEnd: 15, convertStart: 15, convertEnd: 85, muxEnd: 95, uploadEnd: 100 };

/** confirm 済み（ElevenLabs 変換成功後）の mux 失敗時に表示する文言 */
const MUX_FAIL_MESSAGE =
  "動画の合成に失敗しました。音声変換は完了しているため、この処理分の利用時間は消費されます。素材を MP4（H.264 + AAC）で書き出してから再度お試しください。";

/** 合成は成功したが保存だけ失敗した場合の文言（利用時間は消費済み・再試行可） */
const UPLOAD_FAIL_MESSAGE =
  "完成した動画の保存に失敗しました。音声変換は完了しているため、この処理分の利用時間は消費されます。下の「保存を再試行」からもう一度お試しください。";


function fmtSec(s: number) {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
}

function VoiceChangePage() {
  const navigate = useNavigate();
  const presetsFn = useServerFn(listVoicePresets);
  const jobsFn = useServerFn(listVoiceJobs);
  const createJobFn = useServerFn(createVoiceJob);
  const convertFn = useServerFn(convertJobAudio);
  const updateFn = useServerFn(updateVoiceJobStatus);
  const signFn = useServerFn(getVoiceJobSignedUrl);
  const quotaFn = useServerFn(getVoiceQuota);

  const [env, setEnv] = useState<VoiceEnvReport | null>(null);
  const [enginePct, setEnginePct] = useState<number | null>(null);
  const [engineReady, setEngineReady] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [presetId, setPresetId] = useState<string | null>(null);

  const [phase, setPhase] = useState<Phase>("idle");
  const [progress, setProgress] = useState(0);
  const [indeterminate, setIndeterminate] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [statusText, setStatusText] = useState("");
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  /** 合成済みだが保存に失敗した成果物（保存の再試行用） */
  const [pendingUpload, setPendingUpload] = useState<
    { jobId: string; path: string; blob: Blob } | null
  >(null);
  const [retryingUpload, setRetryingUpload] = useState(false);
  /** 同意状況（profiles.verified）: null = 確認中 */
  const [verified, setVerified] = useState<boolean | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const elapsedTimer = useRef<ReturnType<typeof setInterval> | null>(null);


  const { data: presetData, error: presetError } = useQuery({
    queryKey: ["voice-presets"],
    queryFn: () => presetsFn(),
    retry: 2,
  });

  const { data: jobData, refetch: refetchJobs } = useQuery({
    queryKey: ["voice-jobs"],
    queryFn: () => jobsFn(),
  });

  const { data: quotaData, refetch: refetchQuota } = useQuery({
    queryKey: ["voice-quota"],
    queryFn: () => quotaFn(),
  });
  const quota = quotaData?.quota ?? null;
  const dailyRemaining = quota?.daily_remaining_seconds ?? null;
  const monthlyRemaining = quota?.monthly_remaining_seconds ?? null;
  const quotaExhausted = quota ? dailyRemaining === 0 || monthlyRemaining === 0 : false;

  const presets = presetData?.presets ?? [];

  // 作成フローの「声を変える」オプションで選んだボイスを初期選択する
  useEffect(() => {
    if (presetId || presets.length === 0) return;
    const saved = readVoicePresetId();
    if (saved && presets.some((p) => p.id === saved)) setPresetId(saved);
  }, [presets, presetId]);
  const unfinished = useMemo(
    () =>
      (jobData?.jobs ?? []).filter((j) =>
        ["pending", "extracting", "converting", "muxing"].includes(String(j.status)),
      ),
    [jobData],
  );

  // 利用同意（ConsentModal）の確認。v2 未満（旧同意・未同意）は再同意が必要
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data: sessionData } = await supabase.auth.getUser();
      const uid = sessionData.user?.id;
      if (!uid) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("verified, verified_consent_version")
        .eq("user_id", uid)
        .maybeSingle();
      const ok =
        Boolean(profile?.verified) &&
        profile?.verified_consent_version === CONSENT_VERSION;
      if (!cancelled) setVerified(ok);
    })();
    return () => {
      cancelled = true;
    };
  }, []);


  // 環境検出 + 変換エンジンの先行取得
  useEffect(() => {
    const report = detectVoiceEnv();
    setEnv(report);
    if (report.tier === "blocked") return;

    let cancelled = false;
    void (async () => {
      const { registerFfmpegCacheWorker } = await import("@/lib/voiceChange/serviceWorker");
      await registerFfmpegCacheWorker();
      const { prefetchEngine, isEngineCached } = await import("@/lib/voiceChange/ffmpegEngine");
      const cached = await isEngineCached();
      if (!cached) setEnginePct(0);
      try {
        await prefetchEngine((r) => {
          if (!cancelled) setEnginePct(Math.round(r * 100));
        });
        if (!cancelled) {
          setEngineReady(true);
          setEnginePct(null);
        }
      } catch {
        if (!cancelled) setEnginePct(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (elapsedTimer.current) clearInterval(elapsedTimer.current);
    };
  }, []);

  const startElapsed = () => {
    setElapsed(0);
    if (elapsedTimer.current) clearInterval(elapsedTimer.current);
    elapsedTimer.current = setInterval(() => setElapsed((v) => v + 1), 1000);
  };
  const stopElapsed = () => {
    if (elapsedTimer.current) clearInterval(elapsedTimer.current);
    elapsedTimer.current = null;
  };

  const onPick = useCallback(
    async (f: File) => {
      console.info("[voice-change] ui:file-received", {
        timestamp: new Date().toISOString(),
        name: f.name,
        bytes: f.size,
        contentType: f.type,
      });
      setErrorText(null);
      setResultUrl(null);
      if (!f.type.startsWith("video/")) {
        toast.error("動画ファイルを選択してください");
        return;
      }
      const url = URL.createObjectURL(f);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = url;
      const dur = await new Promise<number>((resolve, reject) => {
        v.onloadedmetadata = () => resolve(v.duration);
        v.onerror = () => reject(new Error("動画を読み込めませんでした"));
      }).catch(() => null);
      URL.revokeObjectURL(url);
      const known = Boolean(dur && Number.isFinite(dur));
      if (!known) {
        // 利用時間の集計に長さが必要なため、長さが判定できない動画は受け付けない
        const msg =
          "この動画の長さを確認できませんでした。別の形式（MP4）で書き出してからお試しください。";
        setErrorText(msg);
        toast.error(msg);
        return;
      }
      if (env) {
        const check = checkDuration(env, dur as number);
        if (!check.ok) {
          setErrorText(check.message);
          toast.error(check.message);
          return;
        }
      }
      const seconds = Math.ceil(dur as number);
      if (dailyRemaining !== null && seconds > dailyRemaining) {
        const msg = `この動画（${fmtSec(seconds)}）は本日の残り時間（${fmtSec(dailyRemaining)}）を超えています。`;
        setErrorText(msg);
        toast.error(msg);
        return;
      }
      if (monthlyRemaining !== null && seconds > monthlyRemaining) {
        const msg = `この動画（${fmtSec(seconds)}）は今月の残り時間（${fmtSec(monthlyRemaining)}）を超えています。`;
        setErrorText(msg);
        toast.error(msg);
        return;
      }
      setFile(f);
      setDuration(dur as number);
    },
    [env, dailyRemaining, monthlyRemaining],
  );

  const run = useCallback(async () => {
    if (!file || !presetId || !env || !duration) return;
    setErrorText(null);
    setResultUrl(null);
    setProgress(0);
    setIndeterminate(false);

    let currentJobId: string | null = null;
    try {
      // 0. エンジン読み込み
      setPhase("engine");
      setStatusText("変換エンジンを準備しています…");
      const engine = await import("@/lib/voiceChange/ffmpegEngine");
      await engine.loadEngine();
      setEngineReady(true);

      // 1. 元動画のアップロード（レジューム用に保持）
      const { data: sessionData } = await supabase.auth.getUser();
      const uid = sessionData.user?.id;
      if (!uid) throw new Error("ログインが必要です");
      const ext = file.name.match(/\.[a-z0-9]+$/i)?.[0] ?? ".mp4";
      const inputPath = `${uid}/${Date.now()}${ext}`;
      setStatusText("動画をアップロードしています…");
      const { error: upErr } = await supabase.storage
        .from("videos-input")
        .upload(inputPath, file, { contentType: file.type, upsert: true });
      if (upErr) throw new Error("動画のアップロードに失敗しました");

      const { jobId } = await createJobFn({
        data: {
          inputVideoPath: inputPath,
          voicePresetId: presetId,
          durationSeconds: duration,
        },
      });
      currentJobId = jobId;
      void refetchQuota();

      // 2. 音声抽出 (0 → 15%)
      setPhase("extracting");
      setStatusText("動画から音声を抽出しています…");
      await updateFn({ data: { jobId, status: "extracting", progress: 5 } });
      const audio = await engine.extractAudio(file, (r) =>
        setProgress(Math.round(r * W.extractEnd)),
      );
      setProgress(W.extractEnd);

      // 3. ElevenLabs 変換 (15 → 85%)。streaming 進捗が取れないため経過秒 + 疑似進捗
      setPhase("converting");
      setIndeterminate(true);
      setStatusText("AI が声を変換しています…");
      startElapsed();
      const tick = setInterval(() => {
        setProgress((p) => {
          const cap = W.convertEnd - 2;
          const next = p + (cap - p) * 0.04;
          return Math.min(cap, Math.round(next));
        });
      }, 1000);

      let converted: { convertedPath: string; signedUrl: string | null };
      try {
        const form = new FormData();
        form.append("job_id", jobId);
        form.append("audio", new File([audio], "input.mp3", { type: "audio/mpeg" }));
        converted = await convertFn({ data: form });
      } finally {
        clearInterval(tick);
        stopElapsed();
        setIndeterminate(false);
      }
      setProgress(W.convertEnd);

      // 4. 音声差し替え (85 → 100%)
      await runMux(jobId, file, converted.convertedPath, converted.signedUrl);
    } catch (e) {
      stopElapsed();
      setIndeterminate(false);
      setPhase("error");
      const msg = (e as Error).message || "変換に失敗しました";
      setErrorText(msg);
      toast.error(msg);
      // 変換前に失敗した場合は予約した利用時間を戻す
      if (currentJobId) {
        await updateFn({
          data: { jobId: currentJobId, status: "failed", error: msg.slice(0, 2000) },
        }).catch(() => undefined);
      }
    } finally {
      void refetchJobs();
      void refetchQuota();
    }
  }, [file, presetId, env, duration, createJobFn, updateFn, convertFn, refetchJobs, refetchQuota]);

  /** 完成動画のアップロード → 完了記録。失敗時は再試行できるよう成果物を保持する */
  const uploadOutput = useCallback(
    async (jobId: string, outPath: string, out: Blob) => {
      setStatusText("完成した動画を保存しています…");
      let upPct = W.muxEnd;
      const upTick = setInterval(() => {
        upPct = Math.min(W.uploadEnd - 1, upPct + (W.uploadEnd - 1 - upPct) * 0.15);
        setProgress(Math.round(upPct));
      }, 700);
      const { error: outErr } = await supabase.storage
        .from("videos-output")
        .upload(outPath, out, { contentType: "video/mp4", upsert: true })
        .finally(() => clearInterval(upTick));
      if (outErr) {
        setPendingUpload({ jobId, path: outPath, blob: out });
        throw new Error(UPLOAD_FAIL_MESSAGE);
      }

      setPendingUpload(null);

      await updateFn({
        data: { jobId, status: "completed", progress: 100, outputVideoPath: outPath },
      });
      const { signedUrl: outUrl } = await signFn({
        data: { bucket: "videos-output", path: outPath },
      });
      setResultUrl(outUrl);
      setProgress(W.uploadEnd);
      setPhase("done");
      setErrorText(null);
      setStatusText("完成しました");
      toast.success("音声変換が完了しました");
    },
    [signFn, updateFn],
  );

  const retryUpload = useCallback(async () => {
    if (!pendingUpload || retryingUpload) return;
    setRetryingUpload(true);
    try {
      setPhase("muxing");
      await uploadOutput(pendingUpload.jobId, pendingUpload.path, pendingUpload.blob);
    } catch (e) {
      setPhase("error");
      setErrorText((e as Error).message || UPLOAD_FAIL_MESSAGE);
    } finally {
      setRetryingUpload(false);
      void refetchJobs();
    }
  }, [pendingUpload, retryingUpload, uploadOutput, refetchJobs]);

  const runMux = useCallback(
    async (
      jobId: string,
      videoFile: File,
      convertedPath: string,
      signedUrl: string | null,
    ) => {
      const engine = await import("@/lib/voiceChange/ffmpegEngine");
      setPhase("muxing");
      setStatusText("変換した声を動画に合成しています…");
      // 失敗時の切り分け用に「mux 開始」を DB にも記録する（サーバー側は 70% で止まるため）
      await updateFn({ data: { jobId, status: "muxing", progress: W.convertEnd } });
      const audioUrl =
        signedUrl ??
        (await signFn({ data: { bucket: "audio-converted", path: convertedPath } })).signedUrl;
      const audioBlob = await (await fetch(audioUrl)).blob();


      let out: Blob;
      try {
        out = await engine.muxAudio(videoFile, audioBlob, (r) =>
          setProgress(W.convertEnd + Math.round(r * (W.muxEnd - W.convertEnd))),
        );
      } catch {
        // ElevenLabs 変換は成功しているため、この処理分の利用時間は確定消費済み
        throw new Error(MUX_FAIL_MESSAGE);
      }

      setProgress(W.muxEnd);

      const { data: sessionData } = await supabase.auth.getUser();
      const uid = sessionData.user?.id;
      const outPath = `${uid}/${jobId}.mp4`;
      await uploadOutput(jobId, outPath, out);
    },
    [signFn, updateFn, uploadOutput],
  );


  /** converting 完了後にタブを閉じた場合の再開（muxing から） */
  const resume = useCallback(
    async (job: {
      id: string;
      audio_converted_path: string | null;
      input_video_path: string | null;
    }) => {
      if (!job.audio_converted_path || !job.input_video_path) {
        toast.error("このジョブは最初からやり直してください");
        return;
      }
      try {
        setErrorText(null);
        setProgress(W.convertEnd);
        setPhase("engine");
        setStatusText("変換エンジンを準備しています…");
        const { signedUrl } = await signFn({
          data: { bucket: "videos-input", path: job.input_video_path },
        });
        const blob = await (await fetch(signedUrl)).blob();
        const videoFile = new File([blob], "input.mp4", { type: blob.type || "video/mp4" });
        await runMux(job.id, videoFile, job.audio_converted_path, null);
      } catch (e) {
        setPhase("error");
        setErrorText((e as Error).message || "再開に失敗しました");
      } finally {
        void refetchJobs();
      }
    },
    [signFn, runMux, refetchJobs],
  );

  const busy = ["engine", "extracting", "converting", "muxing"].includes(phase);

  if (env?.tier === "blocked") {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="mb-4 text-2xl font-bold">音声変換</h1>
        <Card className="border-destructive/40 p-6">
          <div className="flex gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-destructive" />
            <div>
              <p className="font-medium">この環境では音声変換をご利用いただけません</p>
              <p className="mt-1 text-sm text-muted-foreground">{env.reason}</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
      <ConsentModal
        open={verified === false}
        onAgreed={() => setVerified(true)}
        onCancel={() => void navigate({ to: "/dashboard" })}
      />

      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Mic2 className="h-6 w-6 text-primary" />
          音声変換（ボイスチェンジ）
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          映像はそのまま、声だけを別の声質に変換します。処理はお使いのブラウザ内で行われます。
        </p>
      </header>

      {/* エンジン準備中 */}
      {enginePct !== null && (
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <div className="flex-1">
              <p className="text-sm font-medium">変換エンジン準備中 (32MB)</p>
              <Progress value={enginePct} className="mt-2 h-2" />
            </div>
            <span className="text-sm tabular-nums text-muted-foreground">{enginePct}%</span>
          </div>
        </Card>
      )}

      {/* 未完了ジョブ */}
      {unfinished.length > 0 && (
        <Card className="p-4">
          <p className="mb-3 text-sm font-semibold">未完了の変換</p>
          <ul className="space-y-2">
            {unfinished.map((j) => (
              <li
                key={j.id}
                className="flex items-center justify-between gap-3 rounded-md border p-3 text-sm"
              >
                <div>
                  <p className="font-medium">
                    {String(j.status) === "muxing" ? "合成待ち" : "処理中"}（{j.progress ?? 0}%）
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(String(j.created_at)).toLocaleString("ja-JP")}
                  </p>
                </div>
                {j.audio_converted_path ? (
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => void resume(j)}>
                    <RotateCcw className="mr-1.5 h-4 w-4" />
                    合成から再開
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">やり直しが必要です</span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {env?.tier === "limited" && (
        <Card className="border-amber-500/40 bg-amber-500/5 p-4">
          <div className="flex gap-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500" />
            <p className="text-sm text-muted-foreground">{env.reason}</p>
          </div>
        </Card>
      )}

      {/* 利用状況 */}
      {quota && (
        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold">今の利用状況</p>
            <span className="text-xs text-muted-foreground">プラン: {quota.plan}</span>
          </div>
          {quota.daily_limit_seconds === 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                現在のプランでは音声変換をご利用いただけません。
              </p>
              <Button asChild size="sm">
                <Link to="/credits-shop">プランを見る</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {(
                [
                  {
                    label: "本日",
                    used: quota.daily_used_seconds,
                    limit: quota.daily_limit_seconds,
                    remaining: quota.daily_remaining_seconds,
                  },
                  {
                    label: "今月",
                    used: quota.monthly_used_seconds,
                    limit: quota.monthly_limit_seconds,
                    remaining: quota.monthly_remaining_seconds,
                  },
                ] as const
              ).map((row) => (
                <div key={row.label}>
                  <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                    <span>
                      {row.label}：残り {fmtSec(row.remaining)}
                    </span>
                    <span>
                      {fmtSec(row.used)} / {fmtSec(row.limit)}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all",
                        row.remaining === 0 ? "bg-destructive" : "bg-primary",
                      )}
                      style={{
                        width: `${Math.min(100, Math.round((row.used / Math.max(1, row.limit)) * 100))}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
              {quotaExhausted && (
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm text-destructive">
                    利用上限に達しました。日付（または月）が変わると再開できます。
                  </p>
                  <Button asChild size="sm" variant="secondary">
                    <Link to="/credits-shop">プランを上げる</Link>
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* 1. ボイス選択 */}
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">1. 変換後のボイスを選ぶ</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {presets.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={busy}
              onClick={() => setPresetId(p.id)}
              className={cn(
                "rounded-lg border p-3 text-left transition-colors",
                presetId === p.id ? "border-primary bg-primary/5" : "hover:bg-muted/50",
              )}
            >
              <span className="block text-sm font-medium">{p.name}</span>
              {p.description && (
                <span className="mt-0.5 block text-xs text-muted-foreground">{p.description}</span>
              )}
            </button>
          ))}
          {presets.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {presetError ? "ボイス一覧を取得できませんでした。再読み込みしてください。" : "ボイスを読み込んでいます…"}
            </p>
          )}

        </div>
      </Card>

      {/* 2. 動画アップロード */}
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">2. 動画を選ぶ</p>
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onPick(f);
          }}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="flex w-full flex-col items-center gap-2 rounded-lg border border-dashed p-6 text-sm text-muted-foreground transition-colors hover:bg-muted/40"
        >
          <UploadCloud className="h-6 w-6" />
          {file ? (
            <span className="font-medium text-foreground">
              {file.name}
              {duration ? `（${fmtSec(duration)}）` : ""}
            </span>
          ) : (
            <span>クリックして動画を選択（MP4 / MOV）</span>
          )}
        </button>
        {errorText && <p className="mt-3 text-sm text-destructive">{errorText}</p>}
        {pendingUpload && (
          <div className="mt-3 space-y-2">
            <Button
              variant="secondary"
              disabled={retryingUpload}
              onClick={() => void retryUpload()}
            >
              {retryingUpload ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              保存を再試行
            </Button>
            <p className="text-xs text-muted-foreground">
              合成済みの動画はこのページのメモリ上にのみ保持されています。タブを閉じる・リロードする・別ページへ移動すると復元できず、消費した利用時間も戻りません。保存が完了するまでこのページを開いたままにしてください。
            </p>
          </div>
        )}

      </Card>

      {/* 3. 実行・進捗 */}
      <Card className="p-4">
        <p className="mb-3 text-sm font-semibold">3. 変換する</p>
        <Button
          className="w-full"
          disabled={!file || !presetId || busy || verified !== true || quotaExhausted || quota?.daily_limit_seconds === 0}
          onClick={() => void run()}
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
          {busy ? "変換中…" : "音声を変換する"}
        </Button>

        {(busy || phase === "done") && (
          <div className="mt-4 space-y-2">
            <Progress
              value={indeterminate ? undefined : progress}
              className={cn("h-2", indeterminate && "animate-pulse")}
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{statusText}</span>
              <span className="tabular-nums">
                {indeterminate ? `経過 ${fmtSec(elapsed)}` : `${progress}%`}
              </span>
            </div>
            {!engineReady && phase === "engine" && (
              <p className="text-xs text-muted-foreground">
                変換エンジン準備中 (32MB)。初回のみ時間がかかります。
              </p>
            )}
          </div>
        )}
      </Card>

      {/* 4. プレビュー・DL */}
      {resultUrl && (
        <Card className="p-4">
          <p className="mb-3 text-sm font-semibold">4. できあがり</p>
          <video src={resultUrl} controls className="w-full rounded-lg bg-black" />
          <Button asChild className="mt-3 w-full" variant="secondary">
            <a href={resultUrl} target="_blank" rel="noopener noreferrer" download>
              <Download className="mr-2 h-4 w-4" />
              ダウンロード
            </a>
          </Button>
        </Card>
      )}
    </div>
  );
}
